import { workspaceTextFor } from './interface-copy';
import { useMemo, useState } from 'react';
import type { DocumentDiff, ParagraphChange } from './document-diff';
import { documentDiffCopy } from './document-diff-copy';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import '../components/assistant-ui/document-diff.css';

export type DocumentDiffProps = {
  model: DocumentDiff;
  emptyLabel?: string;
  paragraphLabel?: string;
  legacyLines?: Array<{
    op: 'equal' | 'add' | 'del' | 'skip' | 'cut';
    text: string;
    count: number;
    added: number;
    removed: number;
  }>;
  legacyChange?: 'added' | 'removed' | 'changed' | 'unchanged';
  locale?: string;
  fullTextAvailable?: boolean;
};

function WordRuns({
  words,
  added,
  removed,
}: {
  words: ParagraphChange['words'];
  added: string;
  removed: string;
}) {
  return (
    <span className="ws-document-diff-words">
      {words.map((word, index) => (
        <span
          key={`${word.kind}:${index}`}
          className={`ws-document-diff-word ws-document-diff-word-${word.kind}`}
        >
          {word.kind !== 'equal' && (
            <span className="ws-sr-only">{word.kind === 'add' ? added : removed}</span>
          )}
          {word.text}
        </span>
      ))}
    </span>
  );
}

function Row({ row, added, removed }: { row: ParagraphChange; added: string; removed: string }) {
  return (
    <p className={`ws-document-diff-row ws-document-diff-row-${row.kind}`} data-change={row.kind}>
      {row.kind === 'add' && <span className="ws-sr-only">{added}</span>}
      {row.kind === 'remove' && <span className="ws-sr-only">{removed}</span>}
      <WordRuns words={row.words} added={added} removed={removed} />
    </p>
  );
}

export function DocumentDiff({
  model,
  emptyLabel,
  paragraphLabel,
  legacyLines,
  legacyChange,
  locale,
  fullTextAvailable = true,
}: DocumentDiffProps) {
  const interfaceLocale = useChatInterfaceLocale();
  const language = locale ?? interfaceLocale;
  const copy = documentDiffCopy(language);
  const text = workspaceTextFor(language);
  const [mode, setMode] = useState<'changes' | 'full'>('changes');
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set());
  const visible = useMemo(() => {
    if (mode === 'full') return model.rows.map((row, index) => ({ row, index }));
    const result: Array<{ row?: ParagraphChange; index: number; unchanged?: number }> = [];
    let index = 0;
    while (index < model.rows.length) {
      if (model.rows[index].kind !== 'equal' || expanded.has(index)) {
        result.push({ row: model.rows[index], index });
        index += 1;
        continue;
      }
      const start = index;
      while (
        index < model.rows.length &&
        model.rows[index].kind === 'equal' &&
        !expanded.has(index)
      )
        index += 1;
      result.push({ index: start, unchanged: index - start });
    }
    return result;
  }, [expanded, mode, model.rows]);
  const changed = model.changedParagraphs;
  return (
    <section className="ws-document-diff" data-testid="document-diff" aria-label={copy.diffView}>
      <div className="ws-document-diff-toolbar">
        <div className="ws-document-diff-segmented" role="group" aria-label={copy.diffView}>
          <button
            type="button"
            aria-pressed={mode === 'changes'}
            onClick={() => setMode('changes')}
          >
            {copy.onlyChanges}
          </button>
          {fullTextAvailable && (
            <button type="button" aria-pressed={mode === 'full'} onClick={() => setMode('full')}>
              {copy.fullText}
            </button>
          )}
        </div>
        <span className="ws-document-diff-stats">
          {copy.stats(changed, model.addedWords, model.removedWords)}
        </span>
      </div>
      {model.truncated && <p className="ws-document-diff-cut">{copy.notShown}</p>}
      {(!model.rows.length && !legacyLines?.length) || (!changed && !legacyLines?.length) ? (
        <p className="ws-muted">{emptyLabel ?? copy.noChanges}</p>
      ) : (
        <div className="ws-document-diff-body">
          {legacyLines ? (
            <div className="ws-page-diff-lines">
              {legacyLines.map((line, index) => {
                if (line.op === 'skip') {
                  const label = text(
                    legacyChange === 'added'
                      ? line.count === 1
                        ? '{count} more line — shown in the complete revision'
                        : '{count} more lines — shown in the complete revision'
                      : legacyChange === 'removed'
                        ? line.count === 1
                          ? '{count} more line removed'
                          : '{count} more lines removed'
                        : line.count === 1
                          ? '{count} unchanged line'
                          : '{count} unchanged lines',
                    { count: line.count.toLocaleString(language) },
                  );
                  return (
                    <div className="ws-page-diff-line" data-op="skip" key={index}>
                      {label}
                    </div>
                  );
                }
                if (line.op === 'cut') {
                  const detail = text(
                    '{count} more lines not shown, {added} added and {removed} removed. Check them in the complete revision.',
                    {
                      count: line.count.toLocaleString(language),
                      added: line.added.toLocaleString(language),
                      removed: line.removed.toLocaleString(language),
                    },
                  );
                  return (
                    <div className="ws-page-diff-line" data-op="cut" key={index}>
                      {detail}
                    </div>
                  );
                }
                const marker = line.op === 'add' ? '+' : line.op === 'del' ? '−' : ' ';
                return (
                  <div className="ws-page-diff-line" data-op={line.op} key={index}>
                    <span className="ws-page-diff-marker" aria-hidden="true">
                      {marker}
                    </span>
                    {line.op !== 'equal' && (
                      <span className="ws-sr-only">
                        {line.op === 'add' ? copy.added : copy.removed}
                      </span>
                    )}
                    <span>{line.text || ' '}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            visible.map((entry) =>
              entry.row ? (
                <Row key={entry.index} row={entry.row} added={copy.added} removed={copy.removed} />
              ) : (
                (() => {
                  const count = 'unchanged' in entry ? (entry.unchanged ?? 0) : 0;
                  return (
                    <button
                      type="button"
                      className="ws-document-diff-skip"
                      key={`skip:${entry.index}`}
                      onClick={() =>
                        setExpanded(
                          (previous) =>
                            new Set([
                              ...previous,
                              ...Array.from({ length: count }, (_, offset) => entry.index + offset),
                            ]),
                        )
                      }
                    >
                      {copy.unchanged(count)}
                    </button>
                  );
                })()
              ),
            )
          )}
        </div>
      )}
    </section>
  );
}

export { DocumentDiff as DiffView };
